import * as React from "react";
import {observer} from "mobx-react";
import {useNavigate} from "react-router-dom";
import {Alert, Box, Grid, TextField} from "@mui/material";
import {useApplication} from "../../../ApplicationProvider";
import {useNavigatorFeature} from "../../../useFeature";
import LayoutContentWrapper from "../../../components/layout/LayoutContentWrapper";
import LayoutContentTitle from "../../../components/layout/LayoutContentTitle";
import FilledButton from "../../../components/buttons/FilledButton";
import LineButton from "../../../components/buttons/LineButton";
import FormSection from "../../../forms/FormSection";
import FormRow from "../../../forms/FormRow";
import FormColumn from "../../../forms/FormColumn";
import FormTextField from "../../../components/fields/FormTextField";
import CuitFormatter from "../../../utils/CuitRenderer";
import FormAutocompleteField from "../../../components/fields/FormAutocompleteField";
import FormAutocompleteMultipleSelectField from "../../../components/fields/FormAutocompleteMultipleSelectField";
import {styles} from "./CreateLeadStyles";
import CreateLeadFeature from "./CreateLeadFeature";
import LeadDeliverySiteScreen from "./LeadDeliverySiteScreen";
import PossibleDuplicateLeadsDialog from "./PossibleDuplicateLeadsDialog";

function CreateLeadScreen() {
    const application = useApplication();
    const navigator = useNavigate();
    const style = styles();
    const feature = useNavigatorFeature(() => new CreateLeadFeature(application), navigator);
    const isFeatureReady = feature && feature.loaded;

    const createdLeadSummary = () => {
        const createdLead = feature.createdLead;
        const outOfZoneNames = createdLead.outOfZoneLeadDeliverySiteNames();
        return (
            <Alert severity="success">
                <div>{feature.createdLeadSuccessMessage}</div>
                {outOfZoneNames.length > 0 && (
                    <div>Puntos de entrega fuera de zona de cobertura: {outOfZoneNames.join(', ')}</div>
                )}
            </Alert>
        );
    };

    const businessSection = (form) => (
        <FormSection title="Datos del negocio">
            <FormColumn>
                <FormRow>
                    <FormTextField label="Nombre de fantasía" form={form} fieldName="fantasyName"/>
                    <FormAutocompleteField
                        label="¿Pertenece a una cadena de franquicias?"
                        form={form}
                        fieldName="hasFranchise"
                        options={feature.optionsFor(feature.yesNoAnswers())}
                    />
                    {feature.shouldAskFranchiseBrandName && (
                        <FormTextField label="¿Qué cadena?" form={form} fieldName="franchiseBrandName"/>
                    )}
                </FormRow>
                <FormRow>
                    <FormAutocompleteField
                        label="Tipo de comercio"
                        form={form}
                        fieldName="commerceType"
                        options={feature.optionsFor(feature.commerceTypes())}
                    />
                    {feature.shouldAskCommerceTypeSpecification && (
                        <FormTextField
                            label="Especificar tipo de comercio"
                            form={form}
                            fieldName="commerceTypeSpecification"
                        />
                    )}
                    <FormAutocompleteMultipleSelectField
                        label="Productos de interés"
                        form={form}
                        fieldName="interestProducts"
                        options={feature.optionsFor(feature.interestProducts())}
                    />
                </FormRow>
            </FormColumn>
        </FormSection>
    );

    const contactSection = (form) => (
        <FormSection title="Contacto">
            <FormRow>
                <FormTextField label="Teléfono" form={form} fieldName="phone"/>
                <FormTextField label="Email" form={form} fieldName="email"/>
            </FormRow>
        </FormSection>
    );

    const originSection = (form) => (
        <FormSection title="Origen">
            <FormColumn>
                <FormRow>
                    <FormAutocompleteField
                        label="Canal de origen"
                        form={form}
                        fieldName="originChannel"
                        options={feature.optionsFor(feature.originChannels())}
                    />
                    <FormAutocompleteField
                        label="¿Cómo nos conoció?"
                        form={form}
                        fieldName="howTheyKnewUs"
                        options={feature.optionsFor(feature.referralSources())}
                    />
                </FormRow>
                {feature.shouldAskReferralObservation && (
                    <FormRow>
                        <FormTextField label="Observaciones" form={form} fieldName="referralObservation" rows={3}/>
                    </FormRow>
                )}
            </FormColumn>
        </FormSection>
    );

    const validationResult = (form) => (
        <FormColumn>
            <FormRow>
                {feature.shouldAskBusinessName
                    ? <FormTextField label="Razón social" form={form} fieldName="businessName"/>
                    : (
                        <Grid item xs={12} sm={6} md={4}>
                            <TextField label="Razón social" value={feature.knownBusinessName} fullWidth disabled/>
                        </Grid>
                    )}
            </FormRow>
            {feature.shouldAskBusinessName && (
                <Alert severity="info">ARCA no encontró el CUIT: cargá la razón social a mano</Alert>
            )}
            {feature.lacksActiveRegistrationCertificate && (
                <Alert severity="info">ARCA informa que el CUIT no tiene constancia activa</Alert>
            )}
            <Alert severity="warning">{feature.customerStatusWarning}</Alert>
        </FormColumn>
    );

    const taxSection = (form) => (
        <FormSection title="Datos fiscales">
            <FormColumn>
                <FormRow>
                    <FormAutocompleteField
                        label="¿Ya tiene CUIT de compra?"
                        form={form}
                        fieldName="hasTaxpayerIdentificationNumber"
                        options={feature.optionsFor(feature.yesNoAnswers())}
                    />
                </FormRow>
                {feature.shouldAskFiscalData && (
                    <FormRow>
                        <FormTextField label="CUIT" form={form} fieldName="taxpayerIdentificationNumber" formatter={new CuitFormatter()}/>
                        <Grid item display="flex" alignItems="center">
                            <LineButton onClick={() => feature.validateTaxpayerIdentificationNumber()}>Validar</LineButton>
                        </Grid>
                    </FormRow>
                )}
                {feature.shouldAskFiscalData && feature.hasValidatedCurrentTaxpayerIdentificationNumber && validationResult(form)}
            </FormColumn>
        </FormSection>
    );

    const locationSection = (form) => (
        <FormSection title="Ubicación">
            <FormColumn>
                <FormRow>
                    <FormAutocompleteField
                        label="¿Ya posee local?"
                        form={form}
                        fieldName="hasPremises"
                        options={feature.optionsFor(feature.yesNoAnswers())}
                    />
                </FormRow>
                {feature.shouldAskLeadDeliverySites && feature.leadDeliverySites.forms.map((leadDeliverySiteForm) => (
                    <LeadDeliverySiteScreen
                        key={leadDeliverySiteForm.formId()}
                        form={leadDeliverySiteForm}
                        onRemove={() => feature.removeLeadDeliverySite(leadDeliverySiteForm)}
                    />
                ))}
                {feature.shouldAskLeadDeliverySites && (
                    <div>
                        <LineButton onClick={() => feature.addLeadDeliverySite()}>Agregar punto de entrega</LineButton>
                    </div>
                )}
            </FormColumn>
        </FormSection>
    );

    const commercialChannelSection = (form) => (
        <FormSection title="Canal comercial">
            <FormRow>
                <FormAutocompleteField
                    label={feature.commercialChannelLabel}
                    form={form}
                    fieldName="commercialChannel"
                    options={feature.optionsFor(feature.commercialChannels())}
                />
            </FormRow>
        </FormSection>
    );

    const content = () => {
        const form = feature.form;
        return (
            <Box sx={style.container}>
                <LayoutContentTitle title="Nuevo lead"/>
                {feature.createdLead && createdLeadSummary()}
                {feature.hasPossibleDuplicateLeads && (
                    <PossibleDuplicateLeadsDialog
                        possibleDuplicateLeadRows={feature.possibleDuplicateLeadRows}
                        selectedPossibleDuplicateLeadId={feature.selectedPossibleDuplicateLeadId}
                        canNotifyResponsible={feature.canNotifyResponsible}
                        onSelect={(possibleDuplicateLeadId) => feature.selectPossibleDuplicateLead(possibleDuplicateLeadId)}
                        onCancel={() => feature.cancelPossibleDuplicateWarning()}
                        onCreateAnyway={() => feature.createLeadAnyway()}
                        onNotifyResponsible={() => feature.notifyResponsibleOfSelectedPossibleDuplicateLead()}
                    />
                )}
                {businessSection(form)}
                {contactSection(form)}
                {originSection(form)}
                {taxSection(form)}
                {locationSection(form)}
                {commercialChannelSection(form)}
                <Box sx={style.buttonsContainer}>
                    <FilledButton disabled={!feature.canCreateLead} onClick={() => feature.createLead()}>
                        Crear
                    </FilledButton>
                </Box>
            </Box>
        );
    };

    return (
        <LayoutContentWrapper isLoading={!isFeatureReady} renderContent={() => content()}/>
    );
}

export default observer(CreateLeadScreen);
